import type { SourceProofMetadata } from '../types/sourceProof';

export interface SourceProofHelper {
  tone: 'info' | 'warning' | 'error';
  title: string;
  message: string;
  meta?: string;
}

export const AUTH_SESSION_BLOCKED_MESSAGE =
  'sign-in is required to refresh live data while mounted proof remains truthful';

export const SAFE_EXCHANGE_REPLAY_BLOCKED_MESSAGE =
  'provider proof cannot be safely replayed on this runner while mounted proof remains truthful';

export function formatSourceProofOccurredAt(value?: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatSourceProofAge(ageMinutes?: number | null): string | null {
  if (ageMinutes == null || !Number.isFinite(ageMinutes)) {
    return null;
  }

  if (ageMinutes < 60) {
    return `${ageMinutes}m old`;
  }

  if (ageMinutes < 24 * 60) {
    return `${Math.round(ageMinutes / 60)}h old`;
  }

  return `${Math.round(ageMinutes / (24 * 60))}d old`;
}

export function getSourceProofHelper(
  proof: SourceProofMetadata | null | undefined,
  options?: {
    degradedMessage?: string | null;
    missingMessage?: string;
    unknownMessage?: string;
    freshMessage?: string;
    staleMessage?: string;
  },
): SourceProofHelper | null {
  const ageLabel = formatSourceProofAge(proof?.ageMinutes);

  if (options?.degradedMessage) {
    return {
      tone: 'error',
      title: 'Showing last truthful proof',
      message: options.degradedMessage,
      meta: ageLabel ?? undefined,
    };
  }

  if (!proof) {
    return options?.missingMessage
      ? {
          tone: 'warning',
          title: 'Proof metadata unavailable',
          message: options.missingMessage,
        }
      : null;
  }

  if (proof.freshnessState === 'stale') {
    return {
      tone: 'warning',
      title: 'Stale but truthful',
      message:
        options?.staleMessage
        ?? `${proof.sourceLabel} is older than Pinecurl's freshness target, but it remains the last truthful proof on this route.`,
      meta: ageLabel ?? undefined,
    };
  }

  if (proof.freshnessState === 'unknown') {
    return {
      tone: 'info',
      title: 'Freshness unavailable',
      message:
        options?.unknownMessage
        ?? `${proof.sourceLabel} is mounted, but Pinecurl cannot calculate the freshness age from this response.`,
    };
  }

  return {
    tone: 'info',
    title: 'Freshness target met',
    message:
      options?.freshMessage
      ?? `${proof.sourceLabel} is the current mounted proof for this decision surface.`,
    meta: ageLabel ?? undefined,
  };
}
